<template>
  <div class="bg-music-panel">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>
    
    <!-- 首次启动提示 -->
    <div v-if="!activated" class="activate-section">
      <div class="activate-icon">
        <svg viewBox="0 0 24 24" width="48" height="48" fill="#FE5E10">
          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
        </svg>
      </div>
      <p class="activate-tip">点击下方按钮启动背景音乐</p>
      <button class="btn-activate" @click="activate">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M8 5v14l11-7z"/>
        </svg>
        启动音乐
      </button>
    </div>

    <!-- 播放控制区 -->
    <div v-else class="player-section">
      <!-- 播放控制 + 曲目信息 组合区 -->
      <div class="player-main">
        <!-- 曲目信息 + 进度条 -->
        <div class="track-info">
          <div class="track-name">
            {{ currentTrack ? currentTrack.name : '未选择曲目' }}
          </div>
          <div class="track-status">{{ isPlaying ? '播放中' : '已暂停' }}</div>
        </div>

        <!-- 进度条 -->
        <div class="progress-bar-wrap">
          <div class="progress-time">
            <span>{{ formatTime(currentTime) }}</span>
          </div>
          <div class="progress-bar" @click="seekTo">
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
              <div class="progress-thumb" :style="{ left: progressPercent + '%' }"></div>
            </div>
          </div>
          <div class="progress-time">
            <span>{{ formatTime(duration) }}</span>
          </div>
        </div>

        <!-- 播放控制按钮 + 音量 -->
        <div class="controls-row">
          <div class="player-controls">
            <button class="ctrl-btn" @click="prevTrack" title="上一首">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
              </svg>
            </button>
            <button class="ctrl-btn play-btn" @click="togglePlay" :title="isPlaying ? '暂停' : '播放'">
              <svg v-if="!isPlaying" viewBox="0 0 24 24" width="26" height="26" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="26" height="26" fill="white">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
              </svg>
            </button>
            <button class="ctrl-btn" @click="nextTrack" title="下一首">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
              </svg>
            </button>
            <button class="ctrl-btn" @click="toggleLoop" :class="{ active: loopMode !== 'none' }" :title="loopModeLabel">
              <svg v-if="loopMode === 'all'" viewBox="0 0 24 24" width="16" height="16" fill="white">
                <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
              </svg>
              <svg v-else-if="loopMode === 'single'" viewBox="0 0 24 24" width="16" height="16" fill="white">
                <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4zm-4-2V9h-1l-2 1v1h1.5v4H13z"/>
              </svg>
              <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="rgba(255,255,255,0.4)">
                <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z"/>
              </svg>
            </button>
          </div>
          <!-- 音量控制（内嵌到控制行） -->
          <div class="volume-control">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="#999" class="volume-icon" @click="toggleMute">
              <path v-if="volume > 0" d="M3 9v6h4l5 5V4L7 9H3z"/>
              <path v-else d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
            <input
              type="range"
              class="volume-slider"
              min="0"
              max="100"
              :value="volume"
              @input="setVolume($event.target.value)"
            />
            <span class="volume-text">{{ volume }}%</span>
          </div>
        </div>
      </div>

      <!-- 曲目列表 -->
      <div class="playlist">
        <div class="playlist-title">播放列表</div>
        <div
          v-for="(track, index) in playlist"
          :key="index"
          class="playlist-item"
          :class="{ active: currentIndex === index, playing: currentIndex === index && isPlaying }"
          @click="playTrack(index)"
        >
          <span class="track-playing-icon" v-if="currentIndex === index && isPlaying">
            <span class="bar"></span><span class="bar"></span><span class="bar"></span>
          </span>
          <span class="track-index" v-else>{{ index + 1 }}</span>
          <span class="track-label">{{ track.name }}</span>
        </div>
        <div v-if="playlist.length === 0" class="empty-playlist">
          暂无曲目，请将音乐文件放入 public/music/ 目录
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * BackgroundMusic - 背景音乐播放器组件（面板模式，嵌入弹框使用）
 *
 * 功能：
 * 1. 解决浏览器自动播放限制：首次需要用户点击激活
 * 2. 播放/暂停/上一首/下一首控制
 * 3. 音量调节、静音切换
 * 4. 循环模式切换（关闭/列表循环/单曲循环）
 * 5. 曲目列表点击切换
 *
 * 使用方式（嵌入 el-dialog）：
 * <el-dialog title="音乐配置" :visible.sync="musicDialogVisible">
 *   <BackgroundMusic :tracks="musicTracks" />
 * </el-dialog>
 */
export default {
  name: 'BackgroundMusic',
  props: {
    /** 音乐列表，格式：[{ name: '曲目名', src: '音频URL' }] */
    tracks: {
      type: Array,
      default: () => []
    },
    /** 初始音量 0-100 */
    initialVolume: {
      type: Number,
      default: 60
    }
  },
  data() {
    return {
      /** 是否已激活（解决浏览器自动播放限制） */
      activated: false,
      /** 是否正在播放 */
      isPlaying: false,
      /** 当前播放索引 */
      currentIndex: 0,
      /** 音量 0-100 */
      volume: this.initialVolume,
      /** 循环模式: none / all / single */
      loopMode: 'all',
      /** Audio 实例 */
      audio: null,
      /** 当前播放时间（秒） */
      currentTime: 0,
      /** 音频总时长（秒） */
      duration: 0
    }
  },
  computed: {
    /** 当前曲目 */
    currentTrack() {
      if (this.playlist.length === 0) return null
      return this.playlist[this.currentIndex] || this.playlist[0]
    },
    /** 播放列表 */
    playlist() {
      return this.tracks
    },
    /** 循环模式文案 */
    loopModeLabel() {
      const labels = { none: '不循环', all: '列表循环', single: '单曲循环' }
      return labels[this.loopMode]
    },
    /** 播放进度百分比 */
    progressPercent() {
      if (!this.duration || this.duration === Infinity) return 0
      return Math.min(100, (this.currentTime / this.duration) * 100)
    }
  },
  watch: {
    volume(val) {
      if (this.audio) {
        this.audio.volume = val / 100
      }
      localStorage.setItem('bg_music_volume', val)
    },
    tracks: {
      handler(newTracks) {
        if (newTracks.length > 0 && this.audio) {
          this.currentIndex = 0
          this.loadTrack(0)
        }
      },
      deep: true
    }
  },
  mounted() {
    this.audio = new Audio()
    this.audio.volume = this.volume / 100
    this.audio.preload = 'auto'

    this.audio.addEventListener('ended', this.onTrackEnded)
    this.audio.addEventListener('error', this.onAudioError)
    this.audio.addEventListener('canplay', this.onCanPlay)
    this.audio.addEventListener('timeupdate', this.onTimeUpdate)
    this.audio.addEventListener('loadedmetadata', this.onLoadedMetadata)

    // 从 localStorage 恢复设置
    const savedVolume = localStorage.getItem('bg_music_volume')
    if (savedVolume !== null) {
      this.volume = parseInt(savedVolume, 10)
    }
    const savedLoop = localStorage.getItem('bg_music_loop')
    if (savedLoop) {
      this.loopMode = savedLoop
    }

    // 从 localStorage 恢复播放状态（刷新页面后自动恢复）
    const savedState = localStorage.getItem('bg_music_state')
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        if (state.activated && this.playlist.length > 0) {
          // 跳过激活界面，直接进入播放器
          this.activated = true
          const trackIndex = Math.min(state.currentIndex || 0, this.playlist.length - 1)
          this.currentIndex = trackIndex
          // 预加载曲目并记录恢复时间点
          this._restoreTime = state.currentTime || 0
          this._shouldResume = state.isPlaying || false
          this.loadTrack(trackIndex)
          return
        }
      } catch (e) {
        // ignore
      }
    }

    // 如果有曲目，预加载第一首
    if (this.playlist.length > 0) {
      this.loadTrack(0)
    }
  },
  beforeUnmount() {
    // 保存当前播放状态到 localStorage
    this.saveState()
    if (this.audio) {
      this.audio.removeEventListener('ended', this.onTrackEnded)
      this.audio.removeEventListener('error', this.onAudioError)
      this.audio.removeEventListener('canplay', this.onCanPlay)
      this.audio.removeEventListener('timeupdate', this.onTimeUpdate)
      this.audio.removeEventListener('loadedmetadata', this.onLoadedMetadata)
      this.audio.pause()
      this.audio.src = ''
      this.audio = null
    }
  },
  methods: {
    /** 激活音频上下文（浏览器要求用户交互后才能播放音频） */
    activate() {
      this.activated = true
      this.$nextTick(() => {
        if (this.playlist.length > 0) {
          this.playTrack(0)
        }
      })
    },

    /** 公开的播放方法，供父组件调用 */
    play() {
      if (!this.activated) {
        this.activate()
        return
      }
      if (this.audio && this.playlist.length > 0) {
        // 如果有待恢复的进度，先设置再播放
        if (this._restoreTime > 0) {
          this.audio.currentTime = this._restoreTime
          this._restoreTime = 0
        }
        this.audio.play().then(() => {
          this.isPlaying = true
          this.saveState()
        }).catch(err => {
          console.warn('播放失败:', err)
        })
      }
    },

    /** 公开的暂停方法，供父组件调用 */
    pause() {
      if (this.audio && this.isPlaying) {
        this.audio.pause()
        this.isPlaying = false
        this.saveState()
      }
    },

    /** 加载指定索引的曲目 */
    loadTrack(index) {
      if (!this.audio || this.playlist.length === 0) return
      if (index < 0 || index >= this.playlist.length) return
      this.currentIndex = index
      const track = this.playlist[index]
      if (track && track.src) {
        this.audio.src = track.src
        // 不手动调用 audio.load()，设置 src 后浏览器会自动加载
        // 手动 load() 可能在 canplay 之后重置 currentTime
      }
    },

    /** 播放指定索引的曲目 */
    playTrack(index) {
      this.loadTrack(index)
      this.$nextTick(() => {
        this.audio.play().then(() => {
          this.isPlaying = true
          this.saveState()
        }).catch(err => {
          console.warn('播放失败:', err)
          this.isPlaying = false
        })
      })
    },

    /** 切换播放/暂停 */
    togglePlay() {
      if (!this.audio || this.playlist.length === 0) return
      if (this.isPlaying) {
        this.audio.pause()
        this.isPlaying = false
      } else {
        this.audio.play().then(() => {
          this.isPlaying = true
        }).catch(err => {
          console.warn('播放失败:', err)
        })
      }
      this.saveState()
    },

    /** 上一首 */
    prevTrack() {
      if (this.playlist.length === 0) return
      let newIndex = this.currentIndex - 1
      if (newIndex < 0) newIndex = this.playlist.length - 1
      this.playTrack(newIndex)
    },

    /** 下一首 */
    nextTrack() {
      if (this.playlist.length === 0) return
      let newIndex = (this.currentIndex + 1) % this.playlist.length
      this.playTrack(newIndex)
    },

    /** 设置音量 */
    setVolume(val) {
      this.volume = parseInt(val, 10)
    },

    /** 静音切换 */
    toggleMute() {
      if (this.volume > 0) {
        this._prevVolume = this.volume
        this.volume = 0
      } else {
        this.volume = this._prevVolume || 60
      }
    },

    /** 切换循环模式：none -> all -> single -> none */
    toggleLoop() {
      const modes = ['none', 'all', 'single']
      const currentIdx = modes.indexOf(this.loopMode)
      this.loopMode = modes[(currentIdx + 1) % modes.length]
      localStorage.setItem('bg_music_loop', this.loopMode)
    },

    /** 曲目播放结束回调 */
    onTrackEnded() {
      switch (this.loopMode) {
        case 'single':
          this.audio.currentTime = 0
          this.audio.play()
          break
        case 'all':
          this.nextTrack()
          break
        case 'none':
          if (this.currentIndex < this.playlist.length - 1) {
            this.nextTrack()
          } else {
            this.isPlaying = false
          }
          break
      }
    },

    /** 音频错误处理 */
    onAudioError(e) {
      console.error('音频加载失败:', e)
      this.isPlaying = false
    },

    /** 音频加载完成后，恢复播放进度 */
    onCanPlay() {
      if (this._restoreTime > 0 && this.audio) {
        this.audio.currentTime = this._restoreTime
        this._restoreTime = 0
        // 不在此处自动播放，由外部 play() 方法触发（需用户交互才能播放）
      }
    },

    /** 播放进度更新 */
    onTimeUpdate() {
      if (this.audio) {
        this.currentTime = this.audio.currentTime
      }
    },

    /** 获取音频总时长 */
    onLoadedMetadata() {
      if (this.audio) {
        this.duration = this.audio.duration
      }
    },

    /** 拖拽/点击进度条跳转 */
    seekTo(e) {
      if (!this.audio) return
      const bar = e.currentTarget
      const rect = bar.getBoundingClientRect()
      let percent = (e.clientX - rect.left) / rect.width
      percent = Math.max(0, Math.min(1, percent))
      this.audio.currentTime = percent * this.duration
      this.currentTime = this.audio.currentTime
    },

    /** 格式化时间为 mm:ss */
    formatTime(seconds) {
      if (!seconds || isNaN(seconds) || seconds === Infinity) return '0:00'
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    /** 保存播放状态到 localStorage */
    saveState() {
      if (!this.activated) return
      const state = {
        activated: true,
        currentIndex: this.currentIndex,
        isPlaying: this.isPlaying,
        currentTime: this.audio ? this.audio.currentTime : 0
      }
      localStorage.setItem('bg_music_state', JSON.stringify(state))
    }
  }
}
</script>

<style lang="scss" scoped>
.bg-music-panel {
  width: 100%;
  user-select: none;
  position: relative;
  overflow: hidden;
  background: #fafafa;
}

/* ========== 背景装饰 ========== */
.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;

  .circle {
    position: absolute;
    border-radius: 50%;
    opacity: 0.06;

    &.circle-1 {
      width: 200px;
      height: 200px;
      background: linear-gradient(135deg, #FE5E10, #ff8a50);
      top: -80px;
      right: -60px;
      animation: floatCircle 8s ease-in-out infinite;
    }
    &.circle-2 {
      width: 120px;
      height: 120px;
      background: linear-gradient(135deg, #ff8a50, #FE5E10);
      bottom: 40px;
      left: -40px;
      animation: floatCircle 10s ease-in-out infinite reverse;
    }
    &.circle-3 {
      width: 80px;
      height: 80px;
      background: #FE5E10;
      bottom: -20px;
      right: 30px;
      animation: floatCircle 6s ease-in-out infinite 1s;
    }
  }
}

@keyframes floatCircle {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(10px, -10px) scale(1.05); }
}

/* ========== 激活区域 ========== */
.activate-section {
  padding: 32px 16px;
  text-align: center;
  position: relative;
  z-index: 1;

  .activate-icon {
    margin-bottom: 16px;
    animation: pulse 2s ease-in-out infinite;

    svg {
      filter: drop-shadow(0 4px 8px rgba(254, 94, 16, 0.3));
    }
  }

  .activate-tip {
    font-size: 13px;
    color: #999;
    margin: 0 0 20px;
    letter-spacing: 0.3px;
  }

  .btn-activate {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 36px;
    background: linear-gradient(135deg, #FE5E10, #ff8a50);
    border: none;
    border-radius: 25px;
    color: white;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 15px rgba(254, 94, 16, 0.3);
    letter-spacing: 0.5px;

    &:hover {
      transform: translateY(-2px) scale(1.02);
      box-shadow: 0 8px 25px rgba(254, 94, 16, 0.4);
    }

    &:active {
      transform: translateY(0) scale(0.98);
      box-shadow: 0 2px 10px rgba(254, 94, 16, 0.3);
    }
  }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.08); opacity: 1; }
}

/* ========== 播放器区域 ========== */
.player-section {
  padding: 0;
  position: relative;
  z-index: 1;
}

.player-main {
  padding: 20px 24px 16px;
}

/* ========== 曲目信息 ========== */
.track-info {
  text-align: center;
  margin-bottom: 14px;

  .track-name {
    font-size: 18px;
    font-weight: 700;
    color: #1a1a1a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 360px;
    margin: 0 auto 8px;
    letter-spacing: 0.5px;
  }

  .track-status {
    font-size: 12px;
    color: #FE5E10;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 12px;
    background: rgba(254, 94, 16, 0.08);
    border-radius: 12px;
    transition: all 0.3s ease;

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #FE5E10;
      animation: pulse 2s infinite;
    }
  }
}

/* ========== 进度条 ========== */
.progress-bar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 16px;
}

.progress-bar {
  flex: 1;
  padding: 8px 0;
  cursor: pointer;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 6px;
  background: #e8e8e8;
  border-radius: 4px;
  overflow: visible;
  transition: height 0.2s ease;
}

.progress-bar:hover .progress-track {
  height: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #FE5E10, #ff8a50);
  border-radius: 4px;
  transition: width 0.15s linear;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(254, 94, 16, 0.6);
}

.progress-thumb {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  background: #FE5E10;
  border: 2px solid #fff;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 0 8px rgba(254, 94, 16, 0.4);
  transition: all 0.2s ease;
  opacity: 0;
  z-index: 10;
}

.progress-bar:hover .progress-thumb {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1.2);
}

.progress-thumb:hover {
  transform: translate(-50%, -50%) scale(1.3);
  box-shadow: 0 0 12px rgba(254, 94, 16, 0.6);
}

.progress-time {
  font-size: 11px;
  color: #999;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  white-space: nowrap;
  min-width: 32px;
}

/* ========== 控制行（播放按钮+音量） ========== */
.controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ========== 播放控制 ========== */
.player-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .ctrl-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: #f8f8f8;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    position: relative;
    overflow: hidden;

    svg {
      fill: #666;
      transition: all 0.3s ease;
    }

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0;
      height: 0;
      background: rgba(254, 94, 16, 0.1);
      border-radius: 50%;
      transform: translate(-50%, -50%);
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 0;
    }

    &:hover {
      background: #f0f0f0;
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

      &::before {
        width: 100%;
        height: 100%;
      }

      svg {
        fill: #FE5E10;
        transform: scale(1.1);
      }
    }

    &:active {
      transform: scale(0.95);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

      &::before {
        width: 120%;
        height: 120%;
      }
    }

    &.active {
      background: rgba(254, 94, 16, 0.12);
      box-shadow: 0 2px 8px rgba(254, 94, 16, 0.15);

      svg { fill: #FE5E10; }

      &:hover {
        background: rgba(254, 94, 16, 0.18);
      }
    }

    &.play-btn {
      width: 54px;
      height: 54px;
      background: linear-gradient(135deg, #FE5E10 0%, #ff8a50 100%);
      border: none;
      box-shadow: 0 4px 16px rgba(254, 94, 16, 0.35);
      position: relative;

      svg {
        fill: white;
        margin-left: 2px;
        z-index: 1;
      }

      &::before {
        background: rgba(255, 255, 255, 0.15);
      }

      &:hover {
        transform: scale(1.12);
        box-shadow: 0 6px 24px rgba(254, 94, 16, 0.45);
      }

      &:active {
        transform: scale(0.95);
        box-shadow: 0 3px 12px rgba(254, 94, 16, 0.3);
      }
    }
  }
}

/* ========== 音量控制 ========== */
.volume-control {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 180px;

  .volume-icon {
    flex-shrink: 0;
    cursor: pointer;
    transition: all 0.3s ease;
    opacity: 0.7;

    &:hover {
      opacity: 1;
      transform: scale(1.1);
    }
  }

  .volume-slider {
    flex: 1;
    min-width: 80px;
    -webkit-appearance: none;
    appearance: none;
    height: 4px;
    border-radius: 2px;
    background: #e8e8e8;
    outline: none;
    transition: all 0.3s ease;

    &:hover {
      height: 5px;
    }

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #FE5E10;
      cursor: pointer;
      border: 2px solid white;
      box-shadow: 0 1px 4px rgba(254, 94, 16, 0.3);
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.2);
        box-shadow: 0 2px 8px rgba(254, 94, 16, 0.4);
      }
    }

    &::-moz-range-thumb {
      width: 14px;
      height: 14px;
      border-radius: 50%;
      background: #FE5E10;
      cursor: pointer;
      border: 2px solid white;
      box-shadow: 0 1px 4px rgba(254, 94, 16, 0.3);
      transition: all 0.3s ease;
    }
  }

  .volume-text {
    font-size: 12px;
    color: #666;
    min-width: 36px;
    text-align: right;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }
}

/* ========== 播放列表 ========== */
.playlist {
  border-top: 1px solid #f0f0f0;
  padding: 16px 16px 12px;
  background: #fff;
  border-radius: 0 0 0 0;
  max-height: 320px;
  overflow-y: auto;

  /* 自定义滚动条 */
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e0e0e0;
    border-radius: 2px;
    &:hover { background: #ccc; }
  }

  .playlist-title {
    font-size: 12px;
    color: #999;
    margin-bottom: 10px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding-left: 4px;
  }

  .playlist-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: #f7f7f7;
      transform: translateX(2px);
    }

    &:active {
      background: #f0f0f0;
      transform: scale(0.98);
    }

    &.active {
      background: linear-gradient(135deg, rgba(254, 94, 16, 0.08) 0%, rgba(255, 138, 80, 0.06) 100%);
      border-left: 3px solid #FE5E10;
      padding-left: 9px;
    }

    &.playing .track-label {
      color: #FE5E10;
      font-weight: 600;
    }

    .track-index {
      font-size: 12px;
      color: #c0c0c0;
      min-width: 22px;
      text-align: center;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
    }

    .track-playing-icon {
      display: flex;
      align-items: flex-end;
      gap: 2px;
      min-width: 22px;
      justify-content: center;
      height: 14px;

      .bar {
        width: 3px;
        background: linear-gradient(180deg, #FE5E10, #ff8a50);
        border-radius: 1.5px;
        animation: soundBar 0.6s ease-in-out infinite alternate;

        &:nth-child(1) { height: 60%; animation-delay: 0s; }
        &:nth-child(2) { height: 100%; animation-delay: 0.2s; }
        &:nth-child(3) { height: 40%; animation-delay: 0.4s; }
      }
    }

    .track-label {
      font-size: 14px;
      color: #444;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: color 0.3s;
    }
  }

  .empty-playlist {
    text-align: center;
    padding: 28px 0;
    font-size: 13px;
    color: #ccc;
  }
}

@keyframes soundBar {
  0% { height: 20%; }
  100% { height: 100%; }
}
</style>
